import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GuidePanel, ContextHelpButton, OnboardingTour, shouldShowInitialTour } from "../components/guide/GuideSystem";
import { pageKeyByPath, type GuidePageKey } from "../config/guideContent";
import { Icon } from "../components/ui/Icon";
import { ActionButton, AlertCard, EmptyState, ErrorState, LoadingState, MetricCard, PageHeader, SearchInput, StatusPill } from "../components/operations/OperationalComponents";
import { CommandPalette } from "../components/command/CommandPalette";
import * as Legacy from "../app/runtime";

const { localPreviewPorts, apiBaseUrl, http, getUser, setAuth, clearAuth, getErr, fmt, fmtDate, fmtDateFull, fmtTime, fmtRel, urgColor, urgLabel, todayStr, startOfDayIso, endOfDayIso, genKey, safePhone, waUrl, mapsUrl, telUrl, openExternal, BRAND_LOGO, TOK, C, EXECUTIVE_ROLES, MANAGER_ROLES, SALES_ROLES, OPS_ROLES, ADMIN_ROLES, WHATSAPP_ROLES, FOLLOW_UP_ROLES, ROLE_LABELS, roleLabel, hasAnyRole, CSS, BrandLogo, ICON_NAMES, renderIcon, Btn, Input, Card, Modal, Toast, Skeleton, Badge, StatusBadge, KPI, SH, Tabs, Row, Empty, useToast, QO, useOrders, useOrder, useCustSrch, useCust, useNotifs, useRules, useCal, useMemory, useFollowUps, useRevenue, useRisks, useStaff, useFeedback, useAssist, useHealth, useUsers, useApprovals, useAudit, useDelivery, useInvoices, useMktRpt, useCharge, useSearch, useInquiries, useGST, useFollowUpTasks, useWhatsAppConversations, useWhatsAppConversation, useWhatsAppAssignableUsers, GlobalSearch, NotifBell, PIPE, StatusPipeline, CancelModal, RescheduleModal, RiderModal, OrderCommandCenter, NewOrderForm, OrderCard, LOST_REASONS, NewInquiryForm, InquiryConversationModal, QuickActions, NAV_MAP } = Legacy;

export const SalesPage = () => {
  const [newOrd, setNewOrd] = useState(false); const [newInq, setNewInq] = useState(false); const [sel, setSel] = useState<any>(null); const [selInq, setSelInq] = useState<any>(null); const [convertInq, setConvertInq] = useState<any>(null);
  const [search, setSearch] = useState(""); const [statusF, setStatusF] = useState(""); const [tab, setTab] = useState("Orders");
  const [ordersPage, setOrdersPage] = useState(1);
  const [inquiriesPage, setInquiriesPage] = useState(1);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("cc-orders-sort") || "delivery-asc");
  const [dateFrom, setDateFrom] = useState(() => localStorage.getItem("cc-orders-date-from") || "");
  const [dateTo, setDateTo] = useState(() => localStorage.getItem("cc-orders-date-to") || "");
  const { data, isLoading, refetch } = useOrders({ search: search || undefined, status: statusF || undefined, limit: 50 });
  const { data: inqData, refetch: refetchInq } = useInquiries({ search: search || undefined, limit: 100 });
  const qc = useQueryClient(); const orders = data?.data || [];
  const inquiries = inqData || [];
  const activeOrders = orders.filter((o: any) => !["DELIVERED", "COLLECTED", "CANCELLED"].includes(o.status));
  const completedOrders = orders.filter((o: any) => ["DELIVERED", "COLLECTED"].includes(o.status));
  const openInquiries = inquiries.filter((i: any) => !["CONVERTED", "LOST"].includes(i.status));
  const lostInquiries = inquiries.filter((i: any) => i.status === "LOST");

  useEffect(() => {
    localStorage.setItem("cc-orders-sort", sortBy);
    localStorage.setItem("cc-orders-date-from", dateFrom);
    localStorage.setItem("cc-orders-date-to", dateTo);
    setOrdersPage(1);
  }, [sortBy, dateFrom, dateTo]);

  const filteredOrders = [...orders]
    .filter((order: any) => {
      if (!dateFrom && !dateTo) return true;

      const raw = order.deliveryDate || order.createdAt;
      if (!raw) return true;

      const value = new Date(raw).getTime();

      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`).getTime();
        if (value < from) return false;
      }

      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`).getTime();
        if (value > to) return false;
      }

      return true;
    })
    .sort((a: any, b: any) => {
      if (sortBy === "delivery-desc") {
        return new Date(b.deliveryDate || 0).getTime() -
          new Date(a.deliveryDate || 0).getTime();
      }

      if (sortBy === "newest") {
        return new Date(b.createdAt || 0).getTime() -
          new Date(a.createdAt || 0).getTime();
      }

      if (sortBy === "oldest") {
        return new Date(a.createdAt || 0).getTime() -
          new Date(b.createdAt || 0).getTime();
      }

      if (sortBy === "customer") {
        return String(a.customerName || "").localeCompare(
          String(b.customerName || ""),
        );
      }

      return new Date(a.deliveryDate || 0).getTime() -
        new Date(b.deliveryDate || 0).getTime();
    });

  const exportOrdersView = () => {
    const rows = [
      [
        "Order",
        "Customer",
        "Phone",
        "Status",
        "Delivery Date",
        "Delivery Time",
        "Total",
        "Balance",
      ],
      ...filteredOrders.map((order: any) => [
        order.orderNumber || "",
        order.customerName || "",
        order.customerPhone || "",
        order.status || "",
        order.deliveryDate || "",
        order.deliveryTime || "",
        String(order.totalPaise ?? ""),
        String(order.balanceDuePaise ?? ""),
      ]),
    ];

    const csv = rows
      .map((row) =>
        row
          .map((value) =>
            `"${String(value).replace(/"/g, '""')}"`
          )
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `orders-view-${todayStr()}.csv`;
    anchor.click();

    URL.revokeObjectURL(url);
  };

  const clearSavedOrderView = () => {
    setSortBy("delivery-asc");
    setDateFrom("");
    setDateTo("");

    localStorage.removeItem("cc-orders-sort");
    localStorage.removeItem("cc-orders-date-from");
    localStorage.removeItem("cc-orders-date-to");
  };

  const ordersPageSize = 24;
  const inquiryPageSize = 25;

  const ordersPageCount = Math.max(
    1,
    Math.ceil(filteredOrders.length / ordersPageSize),
  );

  const inquiriesPageCount = Math.max(
    1,
    Math.ceil(inquiries.length / inquiryPageSize),
  );

  const safeOrdersPage = Math.min(
    ordersPage,
    ordersPageCount,
  );

  const safeInquiriesPage = Math.min(
    inquiriesPage,
    inquiriesPageCount,
  );

  const visibleOrders = filteredOrders.slice(
    (safeOrdersPage - 1) * ordersPageSize,
    safeOrdersPage * ordersPageSize,
  );

  const visibleInquiries = inquiries.slice(
    (safeInquiriesPage - 1) * inquiryPageSize,
    safeInquiriesPage * inquiryPageSize,
  );

  return (
    <div>
      <PageHeader
        eyebrow="Sales desk"
        title="Orders and Inquiries"
        subtitle="Separate new inquiries, active orders, completed work, payment state, production state, and dispatch exceptions before committing the team."
        actions={<><Btn v="secondary" onClick={() => setNewInq(true)} icon="message-circle">New Inquiry</Btn><Btn onClick={() => { setConvertInq(null); setNewOrd(true); }} icon="plus">New Order</Btn></>}
      />
      <div className="op-grid-4" style={{ marginBottom: 16 }}>
        <MetricCard label="Open Inquiries" value={openInquiries.length} tone={C.info} icon="message-circle" detail="needs sales action" />
        <MetricCard label="Active Orders" value={activeOrders.length} tone={C.primary} icon="clipboard-list" detail="not complete/cancelled" />
        <MetricCard label="Completed" value={completedOrders.length} tone={C.success} icon="check" detail="delivered/collected" />
        <MetricCard label="Lost Sales" value={lostInquiries.length} tone={lostInquiries.length ? C.danger : C.success} icon="triangle-alert" detail="reason required" />
      </div>
      <div className="op-filterbar">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as any }}>
          <SearchInput value={search} onChange={setSearch} placeholder="Search customer, phone, order…" />
          <select
            aria-label="Order status"
            value={statusF}
            onChange={e => {
              setStatusF(e.target.value);
              setOrdersPage(1);
            }}
            style={{ padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontFamily: "inherit", outline: "none" }}
          >
            <option value="">All Status</option>
            {Object.entries(C.STATUS).map(([k, v]: any) => <option key={k} value={k}>{v.label}</option>)}
          </select>

          <label style={{ display: "grid", gap: 3, fontSize: 10, color: C.mid }}>
            From
            <input
              aria-label="Delivery date from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ padding: "8px 10px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontFamily: "inherit" }}
            />
          </label>

          <label style={{ display: "grid", gap: 3, fontSize: 10, color: C.mid }}>
            To
            <input
              aria-label="Delivery date to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ padding: "8px 10px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontFamily: "inherit" }}
            />
          </label>

          <select
            aria-label="Sort orders"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{ padding: "8px 12px", border: `1.5px solid ${C.border}`, borderRadius: 10, fontSize: 12, fontFamily: "inherit", outline: "none" }}
          >
            <option value="delivery-asc">Delivery · earliest first</option>
            <option value="delivery-desc">Delivery · latest first</option>
            <option value="newest">Created · newest first</option>
            <option value="oldest">Created · oldest first</option>
            <option value="customer">Customer · A–Z</option>
          </select>

          <Btn size="sm" v="ghost" onClick={clearSavedOrderView}>
            Reset View
          </Btn>

          <Btn size="sm" v="secondary" onClick={exportOrdersView}>
            Export Current View
          </Btn>
        </div>
      </div>
      <Tabs items={["Orders", "Inquiries"]} active={tab} onChange={setTab} />
      {tab === "Inquiries" && (
        <div>
          <Card style={{ marginBottom: 14, background: C.bg }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>Daily Sales CRM</div>
                <div style={{ fontSize: 12, color: C.mid, marginTop: 3 }}>Log every customer inquiry, every conversation update, and every reason a sale did not happen.</div>
              </div>
              <Btn size="sm" onClick={() => setNewInq(true)}>Log Inquiry</Btn>
            </div>
          </Card>
          {visibleInquiries.map((inq: any) => (
            <Card key={inq.id} urgency={inq.status === "LOST" ? C.danger : inq.status === "CONVERTED" ? C.success : C.primary} style={{ marginBottom: 8 }} onClick={() => setSelInq(inq)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" as any }}>
                <div style={{ minWidth: 260, flex: 1 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" as any }}>
                    <span style={{ fontWeight: 800, fontSize: 13 }}>{inq.inquiryNumber}</span>
                    <StatusBadge status={inq.status} />
                    {inq.lostReason && <StatusPill tone="danger">Lost: {inq.lostReason}</StatusPill>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 900, color: C.text, marginTop: 6 }}>{inq.customerName}</div>
                  <div style={{ fontSize: 12, color: C.mid, marginTop: 4 }}>{inq.occasion || "Occasion not captured"} · {inq.source || "Unknown source"} · +91 {inq.phone}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" as any }}>{!["CONVERTED", "LOST"].includes(inq.status) && <Btn size="sm" v="secondary" onClick={(e: any) => { e.stopPropagation(); setConvertInq(inq); setNewOrd(true); }}>Convert</Btn>}<Btn size="sm" v="ghost" onClick={(e: any) => { e.stopPropagation(); setSelInq(inq); }}>Update</Btn></div>
              </div>
              {inq.notes && <div style={{ fontSize: 12, color: C.mid, marginTop: 6 }}>{inq.notes}</div>}
              {inq.conversationUpdates?.[0] && <div style={{ fontSize: 12, color: C.text, marginTop: 8, background: C.bg, borderRadius: 10, padding: "8px 10px" }}>Latest: {inq.conversationUpdates[0].summary}</div>}
            </Card>
          ))}
          {!inquiries.length && <EmptyState icon="clipboard-list" title="No inquiries" description="Capture every customer inquiry before it disappears into chat history." />}

          {inquiries.length > inquiryPageSize && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
              <Btn
                size="sm"
                v="ghost"
                disabled={safeInquiriesPage <= 1}
                onClick={() => setInquiriesPage(Math.max(1, safeInquiriesPage - 1))}
              >
                Previous
              </Btn>

              <span style={{ fontSize: 12, color: C.mid }}>
                Page {safeInquiriesPage} of {inquiriesPageCount} · {inquiries.length} loaded inquiries
              </span>

              <Btn
                size="sm"
                v="ghost"
                disabled={safeInquiriesPage >= inquiriesPageCount}
                onClick={() => setInquiriesPage(Math.min(inquiriesPageCount, safeInquiriesPage + 1))}
              >
                Next
              </Btn>
            </div>
          )}
        </div>
      )}
      {tab === "Orders" && (
        isLoading ? <LoadingState rows={4} /> : !orders.length ? <EmptyState icon="shopping-bag" title="No orders found" description="Confirmed orders will appear here once sales captures the requirements and payment state." /> : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(290px,1fr))", gap: 12 }}>
              {visibleOrders.map((o: any) => <OrderCard key={o.id} order={o} onClick={() => setSel(o)} />)}
            </div>

            {filteredOrders.length > ordersPageSize && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                <Btn
                  size="sm"
                  v="ghost"
                  disabled={safeOrdersPage <= 1}
                  onClick={() => setOrdersPage(Math.max(1, safeOrdersPage - 1))}
                >
                  Previous
                </Btn>

                <span style={{ fontSize: 12, color: C.mid }}>
                  Page {safeOrdersPage} of {ordersPageCount} · {filteredOrders.length} matching orders · {orders.length} loaded
                </span>

                <Btn
                  size="sm"
                  v="ghost"
                  disabled={safeOrdersPage >= ordersPageCount}
                  onClick={() => setOrdersPage(Math.min(ordersPageCount, safeOrdersPage + 1))}
                >
                  Next
                </Btn>
              </div>
            )}
          </>
        )
      )}
      {newOrd && <NewOrderForm
        inquiry={convertInq}
        onClose={() => { setNewOrd(false); setConvertInq(null); }}
        onSuccess={() => { refetch(); refetchInq(); }}
      />}
      {newInq && <NewInquiryForm onClose={() => setNewInq(false)} onSuccess={refetchInq} />}
      {selInq && <InquiryConversationModal inquiry={selInq} onClose={() => setSelInq(null)} onSuccess={refetchInq} />}
      {sel && <OrderCommandCenter orderId={sel.id} onClose={() => { setSel(null); refetch(); }} />}
    </div>
  );
};

export default SalesPage;
